import React, { useState, useEffect, FunctionComponent } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../common/contexts/UserContext";
import { useUserPreferences } from "../../common/contexts/UserPreferencesContext";
import ComponentWrapper from "../../common/components/ComponentWrapper";
import Button from "@murphy-frontend/web-core/components/Button";
import { AlertTemplateCreatorList, TemplateCreatorList } from "@murphy-frontend/web-core/columns";
import { ColumnHeaderCell, ColumnRowCell } from "@murphy-frontend/web-core/components/Table/Models";
import Table from "@murphy-frontend/web-core/components/Table";
import { useGetTemplates as useGetEventFlowTemplates } from "./apiEventflow/queries";
import { useCustomer } from "../../common/contexts/CustomerContext";
import { useDeleteIncidentTemplate } from "./apiEventflow/mutations";
import { getAlertTemplates2 } from "./apiAlert/queries";
import { useDeleteAlertTemplate } from "./apiAlert/mutations";
import Spinner from "@murphy-frontend/web-core/components/Spinner";
import ActionsRow from "./components/ActionsRow";
import { IconContext } from "react-icons/lib"
import { FaCheck } from "react-icons/fa6"
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext";
import { EventFlowTemplateDto } from "./apiEventflow/models";
import { AlertTemplateDTO } from "./apiAlert/models";
import { enabledProducts } from "../../common/variables";

const TemplateCreaterList: FunctionComponent = () => {
    const navigate = useNavigate();
    const { customer } = useCustomer();
    const { openModal, closeModal } = useModal();
    const [templateAlertTableData, setTemplateAlertTableData] = useState<ColumnRowCell[][]>([]);
    const [templateIncidentTableData, setTemplateIncidentTableData] = useState<ColumnRowCell[][]>([]);
    const [alertTemplateColumns, setAlertTemplateColumns] = useState<ColumnHeaderCell[]>([]);
    const [incidentTemplateColumns, setIncidentTemplateColumns] = useState<ColumnHeaderCell[]>([]);
    const { user } = useUser();
    const { data: alertTemplates, isLoading: alertTemplatesIsLoading } = getAlertTemplates2(customer?.Id);
    const { mutate: deleteAlertTemplate, isPending: deleteAlertIsLoading } = useDeleteAlertTemplate();
    const { data: templates, isLoading: templatesIsLoading } = useGetEventFlowTemplates(customer?.Id);
    const { mutate: deleteIncidentTemplate, isPending: deleteIncidentIsLoading } = useDeleteIncidentTemplate(customer?.Id);

    const { translations } = useUserPreferences();

    useEffect(() => {
        if (translations) {
            setAlertTemplateColumns(AlertTemplateCreatorList(translations));
            setIncidentTemplateColumns(TemplateCreatorList(translations));
        }
    }, [translations]);

    useEffect(() => {
        if (translations && templates && templates.length > 0) {
            const templateRowCell: ColumnRowCell[][] = templates.map((template, idx) => [
                {
                    id: template.Id,
                    rowId: idx,
                    displayValue: template.Name,
                    value: template.Name,
                    columnKey: "name"
                },
                {
                    id: template.Id,
                    rowId: idx,
                    displayValue: template.Description,
                    value: template.Description,
                    columnKey: "description"
                },
                {
                    id: template.Id,
                    rowId: idx,
                    displayValue: (
                        <ActionsRow
                            templateId={template.Id}
                            onEditTemplate={onEditEventFlowTemplate}
                            onDeleteTemplate={() => onDelete(template, true)}
                            translations={translations}
                        />
                    ),
                    customClass: "text-center",
                    columnKey: "actions"
                }
            ]);

            setTemplateIncidentTableData(templateRowCell);
        }
    }, [translations, templates]);

    useEffect(() => {
        if (translations && alertTemplates && alertTemplates.length > 0) {
            const alertRowCell: ColumnRowCell[][] = alertTemplates.map((alert, idx) => [
                {
                    id: alert.Id,
                    rowId: idx,
                    displayValue: alert.Name,
                    value: alert.Name,
                    columnKey: "name"
                },
                {
                    id: alert.Id,
                    rowId: idx,
                    displayValue: "",
                    value: "",
                    columnKey: "description"
                },
                {
                    id: alert.Id,
                    rowId: idx,
                    displayValue: alert.Enabled ? (
                        <IconContext.Provider value={{ className: "btn-icon" }}>
                            <FaCheck />
                        </IconContext.Provider>
                    ) : (
                        ""
                    ),
                    value: alert.Enabled,
                    columnKey: "enabled"
                },
                {
                    id: alert.Id,
                    rowId: idx,
                    displayValue: (
                        <ActionsRow
                            templateId={alert.Id}
                            onEditTemplate={onEditAlertTemplate}
                            onDeleteTemplate={() => onDelete(alert, false)}
                            translations={translations}
                        />
                    ),
                    customClass: "text-center",
                    columnKey: "actions"
                }
            ]);

            setTemplateAlertTableData(alertRowCell);
        }
    }, [translations, alertTemplates]);

    function DeleteConfirmationBody({ name }: { name: string }) {
        return (
            <p>
                {translations.delete} <strong>{name}</strong> ?
            </p>
        )
    }

    const onDelete = (template: EventFlowTemplateDto | AlertTemplateDTO, isIncident: boolean) => {
        const modalConf = {
            title: translations["class-remove-file"],
            body: <DeleteConfirmationBody name={template.Name} />,
            hideButton: false,
            buttonText: translations["class-lang-user-table-delete"],
            type: "warning",
            position: 'center',
            okCallback: () => {
                isIncident ? deleteIncidentTemplate(template.Id, {
                    onSuccess: () => navigate('/templatecreator')
                }) : deleteAlertTemplate(template.Id, {
                    onSuccess: () => navigate('/templatecreator')
                })
            },
        };
        openModal(modalConf);
    }

    const onEditEventFlowTemplate = (templateId: number) => {
        navigate(`/createeventflowtemplate?templateId=${templateId}`);
    };

    const onEditAlertTemplate = (templateId: number) => {
        navigate(`/createalerttemplate?templateId=${templateId}`);
    };

    const onClickAddIncidentTemplate = () => {
        navigate('/createeventflowtemplate');
    };

    const onClickAddAlertTemplate = () => {
        navigate('/createalerttemplate');
    };

    return (
        <ComponentWrapper active={8} subMenuActive={5}>
            {(templatesIsLoading || alertTemplatesIsLoading || deleteIncidentIsLoading || deleteAlertIsLoading) ? (
                <Spinner isGlobal />
            ) : (
                <>
                    <section className='header-btns'>
                        <div className='header-back-btn'>
                            <h2>{translations.tmpTemplates || "Templates"}</h2>
                        </div>
                    </section>
                    {user?.mbsEnabled && enabledProducts.mbs === "true" && <section className='table-container-general Alerts'>
                        <div className='header-back-btn'>
                            <h2>{translations.apphsbtncommunication || "Alerts"}</h2>
                            <Button buttonSize={"btn--medium"} onClick={onClickAddAlertTemplate}>
                                {translations.tmpnewalerttemplate || 'Create new alert template'} {/* Adjust translation key */}
                            </Button>
                        </div>
                        <Table columns={alertTemplateColumns} rows={templateAlertTableData} />
                    </section>}
                    {user?.eventFlowsEnabled && <section className='table-container-general Incidents'>
                        <div className='header-back-btn'>
                            <h2>{translations.eventflowsmodulename || "Incidents"}</h2>
                            <Button buttonSize={"btn--medium"} onClick={onClickAddIncidentTemplate}>
                                {translations.tmpnewincidenttemplate || 'Create new incident template'}
                            </Button>
                        </div>
                        <Table columns={incidentTemplateColumns} rows={templateIncidentTableData} />
                    </section>}
                </>
            )}
        </ComponentWrapper>
    );
};

export default TemplateCreaterList;